import { toast } from 'sonner';
import { useParams } from 'react-router-dom';
import { PenToolIcon as Tool } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import EditProfileModal from '@/redux/auth/EditProfile/EditProfileModal';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  useDeleteMachineMutation,
  useGetUserByUsernameQuery,
} from '@/redux/auth/auth.api.slice';
import AddMachineModal from '@/redux/auth/AddMachine/AddMachineModal';
import MachineCard from '@/components/common/MachineCard';
import UserPostsList from '@/redux/post/UserPostsList';
import NotFound from '../NotFound';
import Loader from '@/components/common/Loader';
import { Title } from '@/components/ui/title';
import { selectUserId } from '@/redux/auth/auth.slice';

export default function ProfilePage() {
  const { username } = useParams();
  const currentUserId = useSelector(selectUserId);

  const { data: user, isLoading: isLoadingUser } = useGetUserByUsernameQuery(
    username ?? ''
  );
  const [deleteMachine] = useDeleteMachineMutation();

  const handleDeleteMachine = async (machineId: string) => {
    try {
      await deleteMachine(machineId).unwrap();
      toast.success('Machine deleted successfully');
    } catch (error) {
      toast.error('Failed to delete machine');
    }
  };

  if (isLoadingUser) {
    return <Loader />;
  }

  if (!user) {
    return <NotFound />;
  }

  return (
    <div
      className="px-5 md:px-[5rem] pb-24 pt-[10px]"
      style={{ marginTop: 'var(--nav-height)' }}
    >
      <div className="container mx-auto px-4 max-w-5xl pt-[10px]">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="w-full ">
            <Tabs defaultValue="details" className="w-full h-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Profile Details</TabsTrigger>
                <TabsTrigger value="machines">CNC Machines</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="mt-6">
                <Card>
                  <CardContent className="pt-6 ">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold">Profile Details</h2>
                      <EditProfileModal user={user} />
                    </div>
                    <div className="flex flex-col items-center justify-center mb-10">
                      <div className="relative">
                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-100 shadow-lg">
                          <Avatar className="h-full w-full">
                            <AvatarImage
                              src={user?.profilePicture}
                              alt="profile"
                              width={128}
                              height={128}
                              className="object-cover"
                            />
                            <AvatarFallback>
                              {user.firstname.charAt(0)}
                              {user.lastname.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <Badge className="absolute bottom-0 right-0 bg-gray-800 text-white px-3 py-1">
                          @{user?.username}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">
                            Username
                          </h3>
                          <p className="mt-1 text-lg font-medium">
                            @{user?.username}
                          </p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">
                            Email
                          </h3>
                          <p className="mt-1 text-lg font-medium">
                            {user?.email}
                          </p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">
                            First Name
                          </h3>
                          <p className="mt-1 text-lg font-medium">
                            {user?.firstname}
                          </p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">
                            Last Name
                          </h3>
                          <p className="mt-1 text-lg font-medium">
                            {user?.lastname}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="machines" className="mt-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold">CNC Machines</h2>
                      <AddMachineModal user={user} />
                    </div>

                    {user?.ownedMachines.length === 0 ? (
                      <div className="text-center py-10 text-gray-500">
                        <Tool className="mx-auto h-12 w-12 mb-4" />
                        <p>You haven&apos;t added any CNC machines yet.</p>
                        <p>Click Add Machine to get started.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {user?.ownedMachines.map((machine) => (
                          <MachineCard
                            key={machine._id}
                            id={machine._id}
                            brand={machine.cncBrand.name}
                            model={machine.cncModel.name}
                            variant={machine.name}
                            onDelete={
                              currentUserId === user._id
                                ? handleDeleteMachine
                                : undefined
                            }
                          />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      <div className="mt-8">
        <Title title={`${user.firstname}'s Projects`} />
        <UserPostsList username={user.username} userId={currentUserId} />
      </div>
    </div>
  );
}
